import React from 'react';
import { View, Text } from 'react-native';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import { scale } from '@components/frame';
import i18n from 'i18n-js';
import { isPhoneNative } from '@helpers';

import { GestureResponderEvent, StyleProp, ViewStyle } from 'react-native';
import svgRegister from '@styles/icons/svg/svgRegister';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import { ActionArrow } from '@components/common/ActionArrow';
import { WithColor } from '@styles/colors';

export interface RenderTimeLineProps extends WithColor {
  icon: keyof typeof svgRegister;
  text: string;
  totalTime: number;
  maxValue: number;
  color: string;
  active: number | undefined;
  change: number | undefined;
  setup: number | undefined;
  action?: (event: GestureResponderEvent) => void;
  borderRadiusTop?: boolean;
  borderRadiusBottom?: boolean;
  styles?: {
    mainContainerStyles?: StyleProp<ViewStyle>;
    subContainerStyles?: StyleProp<ViewStyle>;
    leftBlockStyles?: StyleProp<ViewStyle>;
    rightBlockStyles?: StyleProp<ViewStyle>;
  };
  withArrow?: boolean;
  width?: number;
  height?: number;
  timeDifferenceConfig?: {
    timeDifferenceInPercentage: string;
    lessThanTarget: boolean;
  };
  shouldHideTimeline?: boolean;
}

const ProductivityTimeline = ({
  icon,
  text,
  totalTime,
  maxValue,
  color,
  active = 0,
  change = 0,
  setup = 0,
  timeDifferenceConfig,
  C,
  action,
  styles,
  shouldHideTimeline = false,
  withArrow = false,
  width = 14,
  height = 14,
}: RenderTimeLineProps) => {
  const totalPercentage = (totalTime * 100) / maxValue;
  const activePercentage = (active * 100) / totalTime;
  const changePercentage = (change * 100) / totalTime;
  const setupPercentage = (setup * 100) / totalTime;

  const shouldDisplayTimeDifferenceInPercentageBlock =
    !isPhoneNative &&
    timeDifferenceConfig &&
    !!timeDifferenceConfig.timeDifferenceInPercentage &&
    timeDifferenceConfig.timeDifferenceInPercentage !== '0%';

  const content = (
    <View
      style={[
        {
          flexDirection: 'row',
          alignItems: 'center',
          paddingLeft: scale(action ? 15 : 0),
          paddingRight: scale(isPhoneNative ? 0 : 15),
          flex: 1,
          height: '100%',
        },
        withArrow && { gap: scale(isPhoneNative ? 10 : 20) },
        styles?.mainContainerStyles,
      ]}
    >
      <View style={[{ flexDirection: 'row', alignItems: 'center', flex: 1 }, styles?.subContainerStyles]}>
        <View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
            },
            styles?.leftBlockStyles,
          ]}
        >
          <Svg icon={icon} width={scale(width)} height={scale(height)} color={color} />
          <Text numberOfLines={1} style={[font['Button 1'], { color: C(color), marginLeft: scale(5) }]}>
            {i18n.t(text)}
          </Text>
        </View>
        <View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              gap: scale(25),
              flex: !isPhoneNative ? 2 : 1,
              ...(isPhoneNative ? { justifyContent: 'center' } : {}),
            },
            styles?.rightBlockStyles,
          ]}
        >
          {!isPhoneNative && (
            <View style={{ width: scale(60) }}>
              <TimeDisplayInStaticFormat {...{ color, C, timeValue: totalTime }} />
            </View>
          )}
          {!shouldHideTimeline && (
            <View
              style={{
                marginRight: scale(isPhoneNative ? 5 : 0),
                flex: 1,
                borderRadius: scale(4),
                overflow: 'hidden',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <View style={{ height: scale(6), width: `${totalPercentage}%`, flexDirection: 'row', borderRadius: scale(4), overflow: 'hidden' }}>
                <View style={{ height: scale(6), width: `${setupPercentage}%`, backgroundColor: C('Status/Light/Setup') }} />
                <View style={{ height: scale(6), width: `${activePercentage}%`, backgroundColor: C('Status/Light/Active') }} />
                <View style={{ height: scale(6), width: `${changePercentage}%`, backgroundColor: C('Status/Light/Change') }} />
              </View>
              {shouldDisplayTimeDifferenceInPercentageBlock && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(5), marginLeft: scale(10) }}>
                  <Svg
                    width={scale(10)}
                    height={scale(21)}
                    icon="middleArrow"
                    color={C('Label/Light/Tertiary')}
                    {...(timeDifferenceConfig.lessThanTarget ? { transform: 'rotate(180)' } : {})}
                  />
                  <Text style={[font['Subhead 2'], { color: C('Label/Light/Tertiary') }]}>{timeDifferenceConfig.timeDifferenceInPercentage}</Text>
                </View>
              )}
            </View>
          )}
        </View>
        {isPhoneNative && (
          <View style={{ flexShrink: 0 }}>
            <TimeDisplayInStaticFormat {...{ color, C, timeValue: totalTime, styles: { justifyContent: 'flex-end' } }} />
          </View>
        )}
      </View>
      {withArrow && <ActionArrow stylesToReplace={{}} />}
    </View>
  );

  return content;
};

export default ProductivityTimeline;
